/**
 * `*` as a projection item.
 *
 * `SELECT ▮` and `RETURNING ▮` both accept the all-columns wildcard, and it is
 * the single most-typed token at either position — yet `*` is neither a column
 * (so the schema emission never produces it) nor a member of any engine's
 * keyword list (so the keyword emission never does either). Before this
 * emitter existed the only `*` in the whole completion surface was
 * `COUNT(▮` → `*` from `aggregateArgHints`, which left
 * `SELECT DISTINCT ON (customer_id) ▮` (pgsql script.55 §2) and
 * `RETURNING ▮` (sqlite script.54 §1/§2) with no way to reach it.
 *
 * Tier 7 — the keyword tier. `*` is an ADDITION to these positions, never a
 * closed set: the columns the position calls for must keep outranking it, and
 * a caret that has already typed an identifier prefix filters it out in Monaco
 * regardless (no typed word is a prefix of `*`). That makes a coarse
 * position-kind gate safe here — no scan for "am I at a list-item head" is
 * needed to avoid displacing a better answer.
 *
 * Cross-engine parity: `SELECT *` is universal, so the select-list arm is
 * emitted for every engine. The RETURNING arm is NOT — `RETURNING *` is
 * pgsql/sqlite syntax; Oracle's `RETURNING … INTO <var>` takes a bind list
 * (no wildcard) and T-SQL spells the clause `OUTPUT inserted.*`, whose star is
 * a qualified `<pseudo-table>.*` form the dotted-access path owns. Callers on
 * those engines therefore never pass `'returning'`.
 */

import type { CompletionCandidate, CompletionPositionKind, DatabaseEngineId } from '@/core/engines';

/** Engines whose `RETURNING` clause accepts the bare `*` wildcard. */
const RETURNING_STAR_ENGINES: ReadonlySet<DatabaseEngineId> = new Set<DatabaseEngineId>([
  'pgsql',
  'sqlite',
]);

export function emitProjectionWildcard(
  out: CompletionCandidate[],
  positionKind: CompletionPositionKind,
  engine: DatabaseEngineId,
): void {
  const legal =
    positionKind === 'select-list' ||
    (positionKind === 'returning' && RETURNING_STAR_ENGINES.has(engine));
  if (!legal) return;
  out.push({
    label: '*',
    insertText: '*',
    isSnippet: false,
    kind: 'keyword',
    tier: 7,
    detail: 'all columns',
  });
}
